import React from 'react';
import { Task } from '../../types';
import { CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

interface DueDiligenceProgressProps {
  tasks: Task[];
}

export default function DueDiligenceProgress({ tasks }: DueDiligenceProgressProps) {
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress').length;
  const overdueTasks = tasks.filter(t => 
    new Date(t.dueDate) < new Date() && t.status !== 'completed'
  ).length;

  const completionPercentage = totalTasks ? (completedTasks / totalTasks) * 100 : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Overall Progress</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">
              {Math.round(completionPercentage)}%
            </p>
          </div>
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-blue-600" />
          </div>
        </div>
        <div className="mt-4 w-full h-2 bg-gray-200 rounded-full">
          <div
            className="h-2 bg-blue-600 rounded-full"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Completed Tasks</p>
            <p className="mt-1 text-2xl font-semibold text-green-600">
              {completedTasks}
            </p>
          </div>
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Out of {totalTasks} total tasks
        </p>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">In Progress</p>
            <p className="mt-1 text-2xl font-semibold text-yellow-600">
              {inProgressTasks}
            </p>
          </div>
          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
            <Clock className="w-6 h-6 text-yellow-600" />
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Tasks currently in progress
        </p>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Overdue Tasks</p>
            <p className="mt-1 text-2xl font-semibold text-red-600">
              {overdueTasks}
            </p>
          </div>
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Tasks past due date
        </p>
      </div>
    </div>
  );
}